import { EventStatus } from 'app/shared/utils/events';
import { AttendeeState } from 'app/shared/typings';

export interface EventForGetEventStatus {
  isPurchasable?: boolean;
  isSoldOut?: boolean;
  cancelled?: boolean;
  attendeeFlow?: 'buy' | 'apply';
  isAppliable?: boolean;
  competition?: {
    id?: number;
  };
}

export function getEventStatus(
  event?: EventForGetEventStatus | null,
  attendeeState?: AttendeeState
) {
  if (!event) {
    return EventStatus.NONE;
  }

  // even if user has confirmed tickets, cancelled is cancelled
  if (event.cancelled) {
    return EventStatus.CANCELLED;
  }

  const hasConfirmedTickets =
    attendeeState === 'confirmed' || attendeeState === 'reserved';

  // if user has confirmed tickets, paused and sold out are irrelevant
  if (hasConfirmedTickets) {
    return EventStatus.NONE;
  }

  if (event.isSoldOut) {
    return EventStatus.SOLDOUT;
  }

  const isApplyFlowOrCompetition =
    event.attendeeFlow === 'apply' || event.competition?.id;
  const isBuyFlow = !isApplyFlowOrCompetition;
  const isInConfirmableState =
    attendeeState === 'guestlisted' || attendeeState === 'cant_go';
  const isPostInvite = attendeeState === 'expired' || isInConfirmableState;
  const notYetApplied = !attendeeState;

  if (
    (!event.isPurchasable && (isBuyFlow || isPostInvite)) ||
    (isApplyFlowOrCompetition && notYetApplied && !event.isAppliable)
  ) {
    return EventStatus.PAUSED;
  }

  return EventStatus.NONE;
}

export type EventState =
  | 'cancelled'
  | 'paused'
  | 'soldout'
  | 'apply'
  | 'buy'
  | 'confirmed'
  | 'reserved'
  | 'guestlisted'
  | 'cant_go'
  | 'applied'
  | 'expired';

export function calcEventState({
  event,
  attendeeState,
}: {
  event?: EventForGetEventStatus | null;
  attendeeState?: AttendeeState;
}): EventState {
  const eventStatus = getEventStatus(event, attendeeState);

  if (eventStatus === EventStatus.CANCELLED) {
    return 'cancelled';
  }

  if (eventStatus === EventStatus.PAUSED) {
    return 'paused';
  }

  if (eventStatus === EventStatus.SOLDOUT) {
    return 'soldout';
  }

  const isCompetitionEvent = !!event?.competition?.id;

  if (isCompetitionEvent && attendeeState === 'reserved') {
    return 'confirmed';
  }

  if (attendeeState) {
    return attendeeState;
  }

  if (isCompetitionEvent || event?.attendeeFlow === 'apply') {
    return 'apply';
  }

  return 'buy';
}
